import {Injectable} from '@angular/core';

import {environment} from "../../../environments/environment";

import {BehaviorSubject} from "rxjs";
import LaunchDarkly from 'launchdarkly-js-client-sdk';

enum ComparisonType {
	GREATER = "GREATER",
	GREATER_OR_EQUAL = "GREATER_OR_EQUAL",
	LESS_OR_EQUAL = "LESS_OR_EQUAL",
	LESS = "LESS",
	EQUAL = "EQUAL"
}

@Injectable({
	providedIn: 'root'
})
export class LaunchDarklyService {
	private ldClient: any;
	private flagsSubject = new BehaviorSubject<any>({});
	flags$ = this.flagsSubject.asObservable();

	constructor() {
		this.initialize();
	}

	public greaterThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.GREATER);
	}

	public greaterOrEqualThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.GREATER_OR_EQUAL);
	}

	public lessOrEqualThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.LESS_OR_EQUAL);
	}

	public lessThan(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.LESS);
	}

	public equal(flag: string): boolean {
		return this.getComparedFlagsValues(flag, ComparisonType.EQUAL);
	}

	private async initialize(): Promise<void> {
		const clientSideId = environment.ldClientSideId;
		const context = {
			key: environment.ldContextKey,
			name: environment.ldContextName
		};

		this.ldClient = LaunchDarkly.initialize(clientSideId, context);

		this.ldClient.on('change', (changes: any) => {
			const updatedFlags = {...this.flagsSubject.value};
			Object.keys(changes).forEach(flagKey => {
				updatedFlags[flagKey] = changes[flagKey].current;
			});

			this.flagsSubject.next(updatedFlags);
			console.log('Flags atualizadas:', updatedFlags);
		});

		this.ldClient.on('ready', () => {
			console.log('LaunchDarkly client ready!');
			this.flagsSubject.next(this.ldClient.allFlags());
		});

		this.ldClient.on('failed', (error) => {
			console.error('LaunchDarkly failed to initialize:', error);
		});
	}

	private getComparedFlagsValues(flag: string, type: ComparisonType): boolean {
		const versionNumber = this.getVersionNumber(flag);
		let validValue = false;

		this.flags$.forEach(flags => {
			const validFlags: Map<string, boolean> = this.getFlagNameWithoutVersionNumber(flags, flag);
			if (validFlags.has(flag)) {
				validFlags.forEach((flagValue, flagName) => {
					// Feature enabled
					const versionNumberActive = this.getVersionNumber(flagName);
					if (flagValue && versionNumberActive != null && flag != null) {
						if ((type == ComparisonType.GREATER || type == ComparisonType.GREATER_OR_EQUAL) && versionNumberActive > versionNumber) {
							validValue = true;
						}

						if ((type == ComparisonType.LESS || type == ComparisonType.LESS_OR_EQUAL) && versionNumberActive < versionNumber) {
							validValue = true;
						}

						if ((type == ComparisonType.EQUAL || type == ComparisonType.GREATER_OR_EQUAL || type == ComparisonType.LESS_OR_EQUAL) && versionNumber == versionNumberActive) {
							validValue = true;
						}
					}
				});
			}
		});

		return validValue;
	}

	private getVersionNumber(flag: string): number | null {
		const version = flag.replace(/\D+/g, '');

		if (version) {
			return Number(version);
		}

		return null;
	}

	private getFlagNameWithoutVersionNumber(flags: any, flag: string): Map<string, boolean> {
		const noNumbers = flag.replace(/\d+/g, '');
		const validFlags = new Map<string, boolean>();

		Object.keys(flags).forEach(flagVersion => {
			if(flagVersion.replace(/\d+/g, '').includes(noNumbers)){
				validFlags.set(flagVersion, flags[flagVersion]  || false);
			}
		});

		return validFlags;
	}
}
